import { lazy, Suspense, useState, useCallback } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { RefreshCw } from 'lucide-react';
import { Button, LoadingSpinner } from '@/components/ui';
import { useAuth } from '@/contexts/AuthContext';
import { AdminHealthBar } from '@/components/admin/AdminHealthBar';
import { ADMIN_TABS, type AdminTab } from '@/components/admin/shared/constants';
import { cn } from '@/lib/utils';

// Lazy-loaded tab components
const CostApiTab = lazy(() => import('@/components/admin/tabs/CostApiTab'));
const OperationsTab = lazy(() => import('@/components/admin/tabs/OperationsTab'));
const ErrorsTab = lazy(() => import('@/components/admin/tabs/ErrorsTab'));
const UsersTab = lazy(() => import('@/components/admin/tabs/UsersTab'));
const SystemTab = lazy(() => import('@/components/admin/tabs/SystemTab'));
const TwitterTab = lazy(() => import('@/components/admin/tabs/TwitterTab'));

export function AdminMonitoringPage() {
  const { user, isLoading: authLoading } = useAuth();

  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!user || !user.is_master_user) {
    return <Navigate to="/dashboard" replace />;
  }

  return <AdminMonitoringContent />;
}

function AdminMonitoringContent() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab') as AdminTab | null;
  const activeTab: AdminTab = ADMIN_TABS.some(t => t.id === tabParam) ? tabParam! : 'cost-api';

  const [autoRefresh, setAutoRefresh] = useState(true);

  const setActiveTab = useCallback((tab: AdminTab) => {
    setSearchParams({ tab }, { replace: true });
  }, [setSearchParams]);

  const renderTab = () => {
    switch (activeTab) {
      case 'cost-api':
        return <CostApiTab autoRefresh={autoRefresh} />;
      case 'operations':
        return <OperationsTab autoRefresh={autoRefresh} />;
      case 'errors':
        return <ErrorsTab autoRefresh={autoRefresh} />;
      case 'users':
        return <UsersTab autoRefresh={autoRefresh} />;
      case 'system':
        return <SystemTab autoRefresh={autoRefresh} />;
      case 'twitter':
        return <TwitterTab autoRefresh={autoRefresh} />;
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-text-primary">Admin Monitoring</h1>
        <Button
          variant={autoRefresh ? 'primary' : 'secondary'}
          size="sm"
          onClick={() => setAutoRefresh(!autoRefresh)}
        >
          <RefreshCw className={cn('w-4 h-4 mr-2', autoRefresh && 'animate-spin')} />
          {autoRefresh ? 'Auto-refresh ON' : 'Auto-refresh OFF'}
        </Button>
      </div>

      {/* Health Bar */}
      <AdminHealthBar onNavigate={setActiveTab} activeTab={activeTab} />

      {/* Tab Navigation */}
      <div className="border-b border-border">
        <nav className="flex gap-1 -mb-px">
          {ADMIN_TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={cn(
                'px-4 py-2.5 text-sm font-medium rounded-t-lg transition-colors',
                activeTab === tab.id
                  ? 'bg-surface border border-border border-b-surface text-text-primary'
                  : 'text-text-muted hover:text-text-secondary hover:bg-surface-light/50'
              )}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <Suspense
        fallback={
          <div className="flex items-center justify-center py-16">
            <LoadingSpinner size="lg" />
          </div>
        }
      >
        {renderTab()}
      </Suspense>
    </div>
  );
}
